import React, { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import GradientStrip from "@/components/GradientStrip";
import SEO from "@/components/SEO";
import QualiopiBadge from "@/components/QualiopiBadge";
import { api, GCAL_IFRAME } from "@/lib/config";
import { useT } from "@/lib/i18n";
import { Mail, MapPin, Linkedin } from "lucide-react";

export default function Contact() {
  const t = useT();
  const [gcalUrl, setGcalUrl] = useState(GCAL_IFRAME);

  useEffect(() => {
    api.get("/settings/gcal_iframe")
      .then(({ data }) => { if (data.value) setGcalUrl(data.value); })
      .catch(() => {});
  }, []);

  return (
    <Layout headerVariant="dark">
      <SEO
        title="Contact · Engager un échange"
        description="Un premier échange de 30 minutes pour cadrer votre besoin, comprendre votre contexte et voir s'il y a matière à travailler ensemble. Sans engagement, sans posture commerciale."
        path="/contact"
      />
      <section data-testid="contact-hero" className="bg-brand-deep text-white pt-40 md:pt-48 pb-20">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
          <p className="section-num text-white/85 fade-up">{t("contact.eyebrow")}</p>
          <h1 className="display text-5xl md:text-7xl lg:text-8xl leading-[0.95] mt-8 max-w-5xl fade-up-2">
            {t("contact.title")}
          </h1>
          <p className="text-white/90 text-lg md:text-xl leading-relaxed mt-8 max-w-3xl fade-up-3">
            {t("contact.lede")}
          </p>
        </div>
        <GradientStrip />
      </section>

      <section className="bg-paper py-20 md:py-28">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="section-num">{t("contact.sections.coordinates")}</p>
            <h2 className="display text-3xl md:text-4xl leading-tight mt-6">{t("contact.sections.coordinatesTitle")}</h2>
            <div className="space-y-6 mt-10">
              <a href="mailto:william@wedocoaching.com" className="flex items-start gap-4 group" data-testid="contact-email">
                <Mail className="mt-1 text-brand" size={18}/>
                <div>
                  <p className="text-[13px] uppercase tracking-wider text-ink/75">{t("contact.labels.email")}</p>
                  <p className="display text-lg mt-1 editorial-link">william@wedocoaching.com</p>
                </div>
              </a>
              <a href="https://www.linkedin.com/in/william-marques-coach/" target="_blank" rel="noreferrer" className="flex items-start gap-4 group" data-testid="contact-linkedin">
                <Linkedin className="mt-1 text-brand" size={18}/>
                <div>
                  <p className="text-[13px] uppercase tracking-wider text-ink/75">{t("contact.labels.linkedin")}</p>
                  <p className="display text-lg mt-1 editorial-link">William Marques</p>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <MapPin className="mt-1 text-brand" size={18}/>
                <div>
                  <p className="text-[13px] uppercase tracking-wider text-ink/75">{t("contact.labels.interventions")}</p>
                  <p className="display text-lg mt-1">{t("contact.labels.interventionsValue")}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-7">
            <p className="section-num">{t("contact.sections.calendar")}</p>
            <h2 className="display text-3xl md:text-4xl leading-tight mt-6">
              {t("contact.sections.calendarTitle")}
            </h2>
            <p className="text-ink/75 leading-relaxed mt-6 max-w-xl">
              {t("contact.sections.calendarLede")}
            </p>
          </div>
        </div>
      </section>

      <section data-testid="contact-calendar" className="bg-paper-2 py-12 md:py-16">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="bg-paper border border-ink/10 overflow-hidden">
            <iframe
              src={gcalUrl}
              title="Prise de rendez-vous Wedo"
              width="100%"
              height="780"
              style={{ border: 0 }}
              frameBorder="0"
              data-testid="gcal-iframe"
            />
          </div>

          <div className="mt-10 max-w-2xl">
            <QualiopiBadge variant="block" />
          </div>
        </div>
      </section>
    </Layout>
  );
}
